import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { sendFile, UPLOAD_MAX } from "@/lib/files";

export const RemoteCanvas = ({ canvasRef, send, subscribe, visible, zoom = 0, annotate = false, annoColor = "#EF4444" }) => {
  const [drag, setDrag] = useState(false);
  const [sending, setSending] = useState("");
  const dropped = useRef(new Set());
  useEffect(() => subscribe?.((m) => {
    if (m.type === "fs_put_done" && dropped.current.delete(m.req)) toast.success(`Fichier copié sur le PC : ${m.path}`);
    if (m.type === "fs_error") setSending("");
  }), [subscribe]);
  // Drag & drop files from your computer onto the remote screen: they are copied into the remote user's Downloads folder.
  const onDrop = async (e) => {
    e.preventDefault();
    setDrag(false);
    if (!visible) return;
    const items = Array.from(e.dataTransfer.items || []);
    if (items.some((it) => it.webkitGetAsEntry?.()?.isDirectory)) toast.info("Les dossiers s'envoient depuis le panneau Fichiers (« Envoyer un dossier »). Les fichiers sont copiés.");
    const files = Array.from(e.dataTransfer.files || []).filter((f) => f.size > 0 || f.type);
    for (const f of files) {
      if (f.size > UPLOAD_MAX) { toast.error(`${f.name} : trop volumineux (100 Mo maximum)`); continue; }
      await sendFile(send, f, undefined, (p) => setSending(`Copie de ${f.name}… ${p} %`), (req) => dropped.current.add(req));
    }
    setSending("");
  };
  const lastMove = useRef(0);
  const drawing = useRef(false);
  const boxRef = useRef(null);
  const [fit, setFit] = useState(null);
  useEffect(() => {
    const compute = () => {
      const b = boxRef.current, c = canvasRef.current;
      if (!b || !c?.width) return;
      const s = Math.min(b.clientWidth / c.width, b.clientHeight / c.height);
      setFit((f) => (f && Math.abs(f.w - c.width * s) < 1 && Math.abs(f.h - c.height * s) < 1 ? f : { w: c.width * s, h: c.height * s }));
    };
    const ro = new ResizeObserver(compute);
    if (boxRef.current) ro.observe(boxRef.current);
    const t = setInterval(compute, 500);
    return () => { ro.disconnect(); clearInterval(t); };
  }, [canvasRef]);
  // Tell the agent the exact on-screen size: it scales the picture itself (sharp), the browser draws it 1:1.
  useEffect(() => {
    let timer = null, last = "";
    const report = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const b = boxRef.current, dpr = window.devicePixelRatio || 1;
        if (!b) return;
        const v = zoom ? { w: 0, h: 0 } : { w: Math.round(b.clientWidth * dpr), h: Math.round(b.clientHeight * dpr) };
        const key = `${v.w}x${v.h}`;
        if (key !== last) { last = key; send({ type: "viewport", ...v }); }
      }, 400);
    };
    const ro = new ResizeObserver(report);
    if (boxRef.current) ro.observe(boxRef.current);
    report();
    const t = setInterval(() => { last = ""; report(); }, 15000);
    return () => { ro.disconnect(); clearTimeout(timer); clearInterval(t); };
  }, [zoom, visible, send]);
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };
  const onMove = (e) => {
    if (annotate) {
      if (!drawing.current) return;
      send({ type: "annotate", op: "draw", color: annoColor, ...pos(e) });
      return;
    }
    const now = performance.now();
    if (now - lastMove.current < 40) return;
    lastMove.current = now;
    send({ type: "mouse", action: "move", ...pos(e) });
  };
  const onButton = (action) => (e) => {
    e.preventDefault();
    if (annotate) {
      if (action === "down") { drawing.current = true; send({ type: "annotate", op: "draw", color: annoColor, start: true, ...pos(e) }); }
      else { drawing.current = false; send({ type: "annotate", op: "draw", color: annoColor, end: true, ...pos(e) }); }
      return;
    }
    if (action === "down") {
      canvasRef.current.focus();
      // Keep following the pointer outside the canvas so a drag always ends with a release on the remote PC.
      const move = (ev) => onMove(ev);
      const up = (ev) => {
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", up);
        if (ev.target !== canvasRef.current) send({ type: "mouse", action: "up", button: ev.button, ...pos(ev) });
      };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    }
    send({ type: "mouse", action, button: e.button, ...pos(e) });
  };
  const onKey = (action) => (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v") return;
    e.preventDefault();
    send({ type: "key", action, key: e.key });
  };
  const onPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData("text/plain");
    if (text) send({ type: "clipboard_set", text, paste: true });
  };

  const scaled = zoom > 0;
  return (
    <div ref={boxRef} className={`absolute inset-0 ${scaled ? "overflow-auto" : "flex items-center justify-center overflow-hidden"}`}
      onDragOver={(e) => { if (visible && Array.from(e.dataTransfer.types || []).includes("Files")) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setDrag(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) setDrag(false); }}
      onDrop={onDrop} data-testid="remote-drop-zone">
      {drag && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-blue-600/25 border-4 border-dashed border-blue-500 pointer-events-none" data-testid="remote-drop-overlay">
          <p className="rounded-lg bg-white px-5 py-3 text-slate-900 font-semibold shadow">Déposez pour copier dans « Téléchargements » sur le PC distant</p>
        </div>
      )}
      {sending && <p className="absolute top-3 left-1/2 -translate-x-1/2 z-20 rounded-full bg-slate-900/90 text-white text-sm px-4 py-1.5" data-testid="remote-drop-progress">{sending}</p>}
      <canvas
        ref={canvasRef}
        data-testid="remote-screen-canvas"
        tabIndex={0}
        onMouseMove={onMove}
        onMouseDown={onButton("down")}
        onMouseUp={onButton("up")}
        onWheel={(e) => !annotate && send({ type: "mouse", action: "wheel", dy: e.deltaY, ...pos(e) })}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={onKey("down")}
        onKeyUp={onKey("up")}
        onPaste={onPaste}
        style={scaled ? { width: `${(canvasRef.current?.width || 1280) * zoom}px`, maxWidth: "none", maxHeight: "none" } : fit ? { width: `${fit.w}px`, height: `${fit.h}px` } : { maxWidth: "100%", maxHeight: "100%" }}
        className={`outline-none ${annotate ? "cursor-cell" : "cursor-default"} ${visible ? "" : "hidden"}`}
      />
    </div>
  );
};
